// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, AlertCircle, Download, ArrowUp, ArrowDown, Filter, ChevronDown, Pill, Package, CalendarDays, Building2 } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import { resolvePharmacyFromUrl } from '../../auth/pharmacyUrl'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'

interface Props { onScreenEnter: () => void }

interface Branding { name: string; logo_url: string | null; primary_color_hex: string | null }

type SortKey = keyof api.ManufacturerRxItem
type SortDir = 'asc' | 'desc'

/**
 * D-MFR-1 — the external manufacturer portal.
 *
 * A STANDALONE surface (rendered outside the pharmacy shell): it shows the pharmacy's
 * branding, an analytics summary, and the manufacturer's own drug-scoped prescription
 * list — and nothing else. No pharmacy navigation, no support, no secure-share, no
 * notifications. Everything on screen is scoped and masked by the server; this
 * component never filters scope itself.
 */
export default function ManufacturerDashboard({ onScreenEnter }: Props) {
  const { me } = useAuth()
  const [items, setItems] = useState<api.ManufacturerRxItem[]>([])
  // Server-side total for the scoped list; the fetch is clamped at 5000 rows, so
  // total > items.length means the loaded set (and the facet counts derived from
  // it) only covers the newest slice.
  const [serverTotal, setServerTotal] = useState<number | null>(null)
  const [analytics, setAnalytics] = useState<api.ManufacturerAnalytics | null>(null)
  const [phiVisible, setPhiVisible] = useState(false)
  const [branding, setBranding] = useState<Branding | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('filled_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [filters, setFilters] = useState<Partial<Record<SortKey, { text?: string; set?: string[] }>>>({})
  const [openFilter, setOpenFilter] = useState<SortKey | null>(null)
  const filterRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  // Close an open column-filter popover on an outside click.
  useEffect(() => {
    if (!openFilter) return
    const h = (e: MouseEvent) => { if (filterRef.current && !filterRef.current.contains(e.target as Node)) setOpenFilter(null) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [openFilter])

  // Pharmacy branding — fetched from the /p/{code} in the URL, same public endpoint the
  // login page uses. No auth dependency, and it means the portal is branded to the
  // pharmacy the manufacturer was invited by.
  useEffect(() => {
    const res = resolvePharmacyFromUrl()
    if (!res.slug) return
    let cancelled = false
    fetch(`/api/public/pharmacy-branding-by-code/${encodeURIComponent(res.slug)}`)
      .then(r => (r.ok ? r.json() : null))
      .then(b => { if (!cancelled && b?.name) setBranding({ name: b.name, logo_url: b.logo_url ?? null, primary_color_hex: b.primary_color_hex ?? null }) })
      .catch(() => { /* branding is decorative; portal works without it */ })
    return () => { cancelled = true }
  }, [])

  // Data — analytics (aggregates) and the full scoped list (for the table + export),
  // loaded together once. The list search is client-side over the loaded set.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([api.getManufacturerAnalytics(), api.listManufacturerRxAll()])
      .then(([a, rx]) => {
        if (cancelled) return
        setAnalytics(a); setItems(rx.items); setServerTotal(rx.total); setPhiVisible(rx.phi_visible); setError(null)
      })
      .catch(e => { if (!cancelled) setError(friendlyError(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
  }, [])

  const accent = branding?.primary_color_hex || '#134e4a'

  // Columns offered a value-checklist filter (few distinct values); the rest get a
  // "contains" text filter.
  const setCols: SortKey[] = ['drug', 'ndc', 'patient_state', 'state']
  const distinct = useMemo(() => {
    const m: Partial<Record<SortKey, { v: string; n: number }[]>> = {}
    for (const c of setCols) {
      const counts = new Map<string, number>()
      for (const r of items) { const v = cellText(r, c); counts.set(v, (counts.get(v) || 0) + 1) }
      m[c] = [...counts.entries()].map(([v, n]) => ({ v, n })).sort((a, b) => b.n - a.n)
    }
    return m
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const active = Object.keys(filters) as SortKey[]
    return items.filter(r => {
      if (needle) {
        const hay = `${r.drug || ''} ${r.ndc} ${r.state || ''} ${r.patient_state || ''}`.toLowerCase()
        if (!hay.includes(needle)) return false
      }
      for (const key of active) {
        const f = filters[key]; if (!f) continue
        const cell = cellText(r, key)
        if (f.set && f.set.length && !f.set.includes(cell)) return false
        if (f.text && !cell.toLowerCase().includes(f.text.toLowerCase())) return false
      }
      return true
    })
  }, [items, q, filters])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey]
      // Nulls always sort last regardless of direction.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      // filled_at is an ISO string — lexical compare matches chronological.
      let cmp: number
      if (sortKey === 'quantity' || sortKey === 'days_supply') {
        cmp = (parseFloat(String(av)) || 0) - (parseFloat(String(bv)) || 0)
      } else {
        cmp = String(av).localeCompare(String(bv), undefined, { numeric: true })
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return rows
  }, [filtered, sortKey, sortDir])
  // A manufacturer can have thousands of fills across pharmacies; paginate the render so the
  // DOM never holds them all (CSV export below still uses the full `sorted` set).
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(sorted, DEFAULT_PAGE_SIZE, `${sortKey}|${sortDir}|${q}|${JSON.stringify(filters)}`)

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(k); setSortDir(k === 'filled_at' ? 'desc' : 'asc') }
  }

  function exportCsv() {
    const cols: { key: SortKey; label: string }[] = [
      { key: 'patient', label: 'Patient' },
      { key: 'patient_dob', label: 'DOB' }, { key: 'drug', label: 'Drug' },
      { key: 'strength', label: 'Strength' }, { key: 'ndc', label: 'NDC' },
      { key: 'quantity', label: 'Qty' }, { key: 'days_supply', label: 'Days Supply' },
      { key: 'filled_at', label: 'Filled' }, { key: 'patient_state', label: 'Patient State' },
      { key: 'state', label: 'Status' },
    ]
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const header = cols.map(c => c.label).join(',')
    // Export the currently sorted+filtered view, honoring the same masking on screen.
    const lines = sorted.map(r => cols.map(c => esc(
      c.key === 'filled_at' && r.filled_at ? new Date(r.filled_at).toLocaleDateString() : r[c.key]
    )).join(','))
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    const stamp = new Date().toISOString().slice(0, 10)
    a.href = url; a.download = `prescriptions-${stamp}.csv`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const cols: { key: SortKey; label: string; mono?: boolean; mask?: boolean }[] = [
    { key: 'patient', label: 'Patient', mask: true },
    { key: 'patient_dob', label: 'DOB', mask: true },
    { key: 'drug', label: 'Drug' },
    { key: 'ndc', label: 'NDC', mono: true },
    { key: 'quantity', label: 'Qty', mono: true },
    { key: 'days_supply', label: 'Days', mono: true },
    { key: 'filled_at', label: 'Filled' },
    { key: 'patient_state', label: 'State', mono: true },
    { key: 'state', label: 'Status' },
  ]

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Branded header */}
      <header style={{ background: accent, color: '#fff', padding: '16px 24px' }}>
        <div style={{ maxWidth: '100%', margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {branding?.logo_url
            ? <img src={branding.logo_url} alt="" style={{ height: 40, width: 'auto', borderRadius: 6, background: '#fff', padding: 3 }} />
            : <div style={{ height: 40, width: 40, borderRadius: 8, background: 'rgba(255,255,255,.18)', display: 'grid', placeItems: 'center' }}><Building2 size={22} /></div>}
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.1 }}>{branding?.name || 'Specialty Pharmacy'}</div>
            <div style={{ fontSize: 12.5, opacity: 0.85 }}>Manufacturer prescription portal</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, opacity: 0.9 }}>{me?.display_name}</span>
            <span style={{
              fontSize: 11, letterSpacing: '.02em', padding: '3px 9px', borderRadius: 999,
              background: phiVisible ? 'rgba(220,38,38,.85)' : 'rgba(255,255,255,.14)',
              border: `1px solid ${phiVisible ? '#fecaca' : 'rgba(255,255,255,.25)'}`,
            }}>{phiVisible ? 'PATIENT INFO VISIBLE' : 'PATIENT INFO MASKED'}</span>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '100%', margin: '0 auto', padding: '20px 24px 64px' }}>
        {error && (
          <div role="alert" style={{ display: 'flex', gap: 8, padding: '11px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} /><span>{error}</span>
          </div>
        )}

        {/* Analytics dashboard */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <StatCard icon={<Package size={18} />} accent={accent} label="Prescriptions" value={loading ? '—' : (analytics?.total_fills ?? 0).toLocaleString()} />
          <StatCard icon={<Pill size={18} />} accent={accent} label="Distinct drugs (NDC)" value={loading ? '—' : (analytics?.distinct_drugs ?? 0).toLocaleString()} />
          <StatCard icon={<Building2 size={18} />} accent={accent} label="Manufacturers" value={loading ? '—' : (analytics?.distinct_manufacturers ?? 0).toLocaleString()} />
          <StatCard icon={<CalendarDays size={18} />} accent={accent}
            label="Date range"
            value={loading || !analytics?.first_fill ? '—' : `${analytics.first_fill} → ${analytics.last_fill}`}
            small />
        </section>

        {!loading && analytics && (analytics.by_drug.length > 0 || analytics.by_month.length > 0) && (
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 20 }}>
            {analytics.by_month.length > 0 && (
              <Panel title="Prescriptions by month">
                <MonthlyBars data={analytics.by_month} accent={accent} />
              </Panel>
            )}
            {analytics.by_drug.length > 0 && (
              <Panel title="Top drugs by prescriptions">
                <TopBars data={analytics.by_drug.map(d => ({ label: d.drug || d.ndc, sub: d.ndc, value: d.fills }))} accent={accent} />
              </Panel>
            )}
            {analytics.by_state.length > 0 && (
              <Panel title="By patient state">
                <TopBars data={analytics.by_state.map(s => ({ label: s.state, value: s.fills }))} accent={accent} />
              </Panel>
            )}
          </section>
        )}

        {/* Prescription list */}
        <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Prescriptions</h2>
            <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 380 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search drug, NDC, or state…" autoComplete="off"
                style={{ width: '100%', padding: '7px 11px 7px 32px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13.5 }} />
            </div>
            <span style={{ fontSize: 12.5, color: '#64748b' }}>{loading ? 'Loading…' : `${sorted.length.toLocaleString()} shown`}</span>
            <button onClick={exportCsv} disabled={loading || sorted.length === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', padding: '7px 12px', borderRadius: 7, border: `1px solid ${accent}`, background: accent, color: '#fff', fontSize: 13, fontWeight: 600, cursor: (loading || sorted.length === 0) ? 'default' : 'pointer', opacity: (loading || sorted.length === 0) ? 0.5 : 1 }}>
              <Download size={14} /> Export CSV
            </button>
          </div>

          {/* Partial-set notice (same visual language as the StationQueue truncated banner):
              the fetch is clamped at 5000 rows, so warn when the server holds more —
              and the column-filter facet counts only reflect the loaded slice. */}
          {!loading && serverTotal !== null && serverTotal > items.length && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, padding: '7px 10px', margin: '8px 16px 0', borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#78350f' }} role="status">
              Showing the newest {items.length.toLocaleString()} of {serverTotal.toLocaleString()} prescriptions — refine filters to narrow. Column filter counts reflect only the loaded rows.
            </div>
          )}
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="prescriptions" />
          <div style={{ overflowX: 'auto' }}>
            {!loading && sorted.length === 0 && !error ? (
              <div style={{ padding: 44, textAlign: 'center', color: '#64748b', fontSize: 13.5 }}>
                {q.trim() ? `No prescriptions match “${q.trim()}”.` : 'No prescriptions to show for your assigned drugs yet.'}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  {/* Row 1 — sortable column labels */}
                  <tr>
                    {cols.map(c => {
                      const active = sortKey === c.key
                      return (
                        <th key={c.key} onClick={() => toggleSort(c.key)}
                          style={{ textAlign: 'left', padding: '10px 14px 6px', background: '#f8fafc', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: active ? accent : '#64748b', fontWeight: 700 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {c.label}
                            {active && (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                          </span>
                        </th>
                      )
                    })}
                  </tr>
                  {/* Row 2 — a visible filter control under every column */}
                  <tr>
                    {cols.map(c => {
                      const f = filters[c.key]
                      const hasFilter = !!(f?.text || f?.set?.length)
                      const isSet = setCols.includes(c.key)
                      return (
                        <th key={c.key} style={{ padding: '0 10px 10px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0', position: 'relative', verticalAlign: 'top' }}>
                          {isSet ? (
                            <>
                              <button onClick={() => setOpenFilter(o => (o === c.key ? null : c.key))} title={`Filter ${c.label}`}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, width: '100%', justifyContent: 'space-between', padding: '5px 8px', border: `1px solid ${hasFilter ? accent : '#cbd5e1'}`, background: hasFilter ? `${accent}14` : '#fff', color: hasFilter ? accent : '#475569', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500, boxSizing: 'border-box' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
                                  <Filter size={11} fill={hasFilter ? accent : 'none'} />
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f?.set?.length ? `${f.set.length} selected` : 'All'}</span>
                                </span>
                                <ChevronDown size={12} style={{ flexShrink: 0 }} />
                              </button>
                              {openFilter === c.key && (
                                <div ref={filterRef} style={{ position: 'absolute', top: '100%', left: 10, zIndex: 30, marginTop: 2, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.12)', padding: 10, minWidth: 210, textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#0f172a' }}>
                                  <SetFilter options={distinct[c.key] || []} selected={f?.set || []} accent={accent}
                                    onChange={sel => setFilters(prev => ({ ...prev, [c.key]: sel.length ? { set: sel } : undefined }))} />
                                  {hasFilter && (
                                    <button onClick={() => setFilters(prev => ({ ...prev, [c.key]: undefined }))}
                                      style={{ marginTop: 8, fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>
                                  )}
                                </div>
                              )}
                            </>
                          ) : (
                            <input value={f?.text || ''} placeholder="Filter…"
                              onChange={e => { const v = e.target.value; setFilters(prev => ({ ...prev, [c.key]: v ? { text: v } : undefined })) }}
                              style={{ width: '100%', padding: '5px 8px', border: `1px solid ${hasFilter ? accent : '#cbd5e1'}`, borderRadius: 6, fontSize: 12, color: '#0f172a', background: '#fff', boxSizing: 'border-box' }} />
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(r => (
                    <tr key={r.rx_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {cols.map(c => {
                        const raw = c.key === 'filled_at' && r.filled_at ? new Date(r.filled_at).toLocaleDateString() : r[c.key]
                        const masked = c.mask && !phiVisible
                        return (
                          <td key={c.key} style={{ padding: '9px 14px', verticalAlign: 'middle', fontFamily: c.mono ? 'ui-monospace, monospace' : undefined, whiteSpace: c.key === 'drug' ? 'normal' : 'nowrap' }}>
                            {raw == null || raw === '' ? <span style={{ color: '#cbd5e1' }}>—</span>
                              : masked ? <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#64748b', background: '#f1f5f9', border: '1px dashed #cbd5e1', borderRadius: 4, padding: '1px 6px' }}>{String(raw)}</span>
                              : <span>{String(raw)}</span>}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="prescriptions" />
        </section>

        <p style={{ marginTop: 14, fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
          You see prescriptions dispensed under the drugs granted to your account, and nothing else.
          {!phiVisible && ' Patient names, record numbers and dates of birth are reduced before they leave the pharmacy.'}
        </p>
      </main>
    </div>
  )
}

// The value shown in a cell, as a string — used by both rendering and the
// column filters so a filter matches exactly what the user sees (masked columns
// filter on their masked value; dates on the formatted date).
function cellText(r: api.ManufacturerRxItem, key: SortKey): string {
  const raw = key === 'filled_at' && r.filled_at ? new Date(r.filled_at).toLocaleDateString() : r[key]
  return raw == null || raw === '' ? '—' : String(raw)
}

function SetFilter({ options, selected, onChange, accent }: { options: { v: string; n: number }[]; selected: string[]; onChange: (s: string[]) => void; accent: string }) {
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v])
  return (
    <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
      {options.length === 0 && <span style={{ fontSize: 12.5, color: '#94a3b8' }}>No values</span>}
      {options.map(o => (
        <label key={o.v} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, padding: '3px 2px', cursor: 'pointer' }}>
          <input type="checkbox" checked={selected.includes(o.v)} onChange={() => toggle(o.v)} style={{ accentColor: accent }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.v}</span>
          <span style={{ color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{o.n}</span>
        </label>
      ))}
    </div>
  )
}

function StatCard({ icon, label, value, accent, small }: { icon: React.ReactNode; label: string; value: string; accent: string; small?: boolean }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: accent, marginBottom: 6 }}>
        {icon}<span style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</span>
      </div>
      <div style={{ fontSize: small ? 15 : 26, fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>{value}</div>
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#334155', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  )
}

function TopBars({ data, accent }: { data: { label: string; sub?: string; value: number }[]; accent: string }) {
  const max = Math.max(1, ...data.map(d => d.value))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
            <span style={{ fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '78%' }}>
              {d.label}{d.sub && <span style={{ color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}> · {d.sub}</span>}
            </span>
            <span style={{ fontWeight: 700, color: '#334155' }}>{d.value.toLocaleString()}</span>
          </div>
          <div style={{ height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(d.value / max) * 100}%`, background: accent, borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function MonthlyBars({ data, accent }: { data: { month: string | null; fills: number }[]; accent: string }) {
  const max = Math.max(1, ...data.map(d => d.fills))
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, paddingTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <div style={{ fontSize: 10.5, color: '#64748b', fontWeight: 600 }}>{d.fills}</div>
          <div title={`${d.month}: ${d.fills}`} style={{ width: '100%', maxWidth: 34, height: `${Math.max(4, (d.fills / max) * 100)}%`, background: accent, borderRadius: '4px 4px 0 0', minHeight: 4 }} />
          <div style={{ fontSize: 9.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{(d.month || '').slice(2)}</div>
        </div>
      ))}
    </div>
  )
}
